import { FC, useCallback, useEffect, useMemo } from "react";
import { NavLink } from "react-router";
import { observer } from "mobx-react";
import { Box, Tooltip, Typography } from "@mui/material";
import { RiInformation2Line } from "react-icons/ri";
import dayjs from "dayjs";

import CoreCard from "../../../ui/core/CoreCard";
import CoreVirtualizedTable from "../../../ui/core/CoreVirtualizedTable";
import { useObservabilityStore } from "../../../../hooks/useObservabilityStore";
import { formatCivilDate, formatInstant } from "../../../helpers/dataHelper";
import { formatCurrency } from "./MetricTiles";
import type { SuccessFeePerClaimRow } from "../../../../types/observability";
import type {
  ColumnFilterState,
  ColumnFilterType,
  ColumnFiltersConfig,
  SimpleColumnDef,
} from "../../../../types/table";
import { PERMISSIONS } from "../../../../types/auth";
import { useHasPermission } from "../../../../contexts/permissions/useHasPermission";
import UsageDownloadCsv from "./UsageDownloadCsv";
import useNotifier from "../../../../hooks/useNotifier";
import SuccessFeeByClaimColumnVisibilityButton from "./SuccessFeeByClaimColumnVisibilityButton";

const ROW_HEIGHT = 48;

const renderMoneyCell = (value: number | null | undefined) => (
  <Typography noWrap sx={{ textAlign: "right", width: "100%" }}>
    {typeof value === "number" ? formatCurrency(value) : "—"}
  </Typography>
);

const renderTextCell = (value: string | null | undefined) => (
  <Typography noWrap title={value ?? ""} sx={{ textAlign: "left" }}>
    {value || "—"}
  </Typography>
);

const renderDateCell = (value: string | null | undefined) => (
  <Typography noWrap sx={{ textAlign: "left" }}>
    {value ? formatInstant(value, { dateOnly: true }) : "—"}
  </Typography>
);

// Plain calendar dates (YYYY-MM-DD): no timezone shift, same day as the CSV.
const renderCivilDateCell = (value: string | null | undefined) => (
  <Typography noWrap sx={{ textAlign: "left" }}>
    {value ? formatCivilDate(value) : "—"}
  </Typography>
);

// Client-side filtering: the set is small and unpaginated (same premise as
// its client-side sorting), so filters run in-memory instead of round-tripping
// to the backend like the activity feed's column filters do.
const FILTERABLE_COLUMNS = [
  "claim_number",
  "appeal_date",
  "payer",
  "user_displayName",
  "payment_date",
  "paid_at_appeal",
  "paid_now",
  "success_fee",
] as const;

const COLUMN_FILTER_TYPES: Record<string, ColumnFilterType> = {
  claim_number: "text",
  appeal_date: "dateRange",
  payer: "text",
  user_displayName: "list",
  payment_date: "dateRange",
  paid_at_appeal: "priceRange",
  paid_now: "priceRange",
  success_fee: "priceRange",
};

const uniqueColumnValues = (
  rows: SuccessFeePerClaimRow[],
  key: "claim_number" | "payer" | "user_displayName",
) => Array.from(new Set(rows.map((row) => row[key]).filter(Boolean)));

// Table column id -> key in the success-fees-per-claim options response.
const OPTIONS_KEY_BY_COLUMN: Record<string, string> = {
  claim_number: "claim_number",
  payer: "payer",
  user_displayName: "user",
};

// List filter over whole values. A row has exactly one, so "and" over several
// can only mean "any of"; "isnot" excludes.
const matchesListFilter = (
  value: string | number | null | undefined,
  filter: ColumnFilterState,
) => {
  if (!filter.values.length) return true;
  const selected = filter.values.includes(String(value ?? ""));
  return filter.operator === "isnot" ? !selected : selected;
};

// Text filter: multiple values are tags added by the user; "and" requires
// every tag to appear, "or" requires any one to.
const matchesTextFilter = (
  value: string | null | undefined,
  filter: ColumnFilterState,
) => {
  if (!filter.values.length) return true;
  const haystack = (value ?? "").toLowerCase();
  const needles = filter.values.map((needle) => needle.toLowerCase());
  return filter.operator === "and"
    ? needles.every((needle) => haystack.includes(needle))
    : needles.some((needle) => haystack.includes(needle));
};

// Price/date range filter: mirrors the popover's own copy — "min" alone is
// an exact match, "min" + "max" is an inclusive range, "max" alone is an
// upper bound.
const matchesRangeFilter = (
  value: number | null | undefined,
  [min, max]: string[] = [],
) => {
  if (!min && !max) return true;
  if (value === null || value === undefined) return false;
  if (min && max) return value >= Number(min) && value <= Number(max);
  if (min) return value === Number(min);
  return value <= Number(max);
};

// Plain YYYY-MM-DD dates are civil days already; timestamps are compared on
// the viewer's local day, matching what the table renders.
const toFilterDay = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : dayjs(value).format("YYYY-MM-DD");

const matchesDateFilter = (
  value: string | null | undefined,
  [start, end]: string[] = [],
) => {
  if (!start && !end) return true;
  if (!value) return false;
  const date = toFilterDay(value);
  if (start && end) return date >= start && date <= end;
  if (start) return date === start;
  return date <= end!;
};

const rowMatchesFilters = (
  row: SuccessFeePerClaimRow,
  filters: Record<string, ColumnFilterState>,
) => {
  if (
    filters.claim_number &&
    !matchesTextFilter(row.claim_number, filters.claim_number)
  ) {
    return false;
  }
  if (
    filters.appeal_date &&
    !matchesDateFilter(row.appeal_date, filters.appeal_date.values)
  ) {
    return false;
  }
  if (filters.payer && !matchesTextFilter(row.payer, filters.payer)) {
    return false;
  }
  if (
    filters.user_displayName &&
    !matchesListFilter(row.user_id, filters.user_displayName)
  ) {
    return false;
  }
  if (
    filters.payment_date &&
    !matchesDateFilter(row.payment_date, filters.payment_date.values)
  ) {
    return false;
  }
  if (
    filters.paid_at_appeal &&
    !matchesRangeFilter(row.paid_at_appeal, filters.paid_at_appeal.values)
  ) {
    return false;
  }
  if (
    filters.paid_now &&
    !matchesRangeFilter(row.paid_now, filters.paid_now.values)
  ) {
    return false;
  }
  if (
    filters.success_fee &&
    !matchesRangeFilter(row.success_fee, filters.success_fee.values)
  ) {
    return false;
  }
  return true;
};

// Column definitions for the table, filtered to the visible ones, pinned first.
const useSuccessFeesPerClaimColumns = () => {
  const {
    successFeesPerClaimColumnVisibility,
    isSuccessFeesPerClaimColumnVisible,
    successFeesPerClaimColumnPins,
    isSuccessFeesPerClaimColumnPinned,
  } = useObservabilityStore();

  const columns: SimpleColumnDef<SuccessFeePerClaimRow>[] = useMemo(
    () => [
      {
        id: "claim_number",
        header: "Claim",
        accessorKey: "claim_number",
        minWidth: 110,
        enableSorting: true,
        cell: (value: string) =>
          value ? (
            <NavLink to={`/claims?claim_number=${value}`}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 600,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {value}
              </Typography>
            </NavLink>
          ) : (
            "—"
          ),
      },
      {
        id: "appeal_date",
        header: "Appealed",
        accessorKey: "appeal_date",
        minWidth: 110,
        enableSorting: true,
        cell: renderDateCell,
      },
      {
        id: "payer",
        header: "Payer",
        accessorKey: "payer",
        minWidth: 120,
        enableSorting: true,
        cell: renderTextCell,
      },
      {
        id: "user_displayName",
        header: "User",
        accessorKey: "user_displayName",
        minWidth: 140,
        enableSorting: true,
        cell: renderTextCell,
      },
      {
        id: "paid_at_appeal",
        header: "Paid @ Appeal",
        accessorKey: "paid_at_appeal",
        minWidth: 110,
        enableSorting: true,
        cell: renderMoneyCell,
      },
      {
        id: "paid_now",
        header: "Paid Now",
        accessorKey: "paid_now",
        minWidth: 110,
        enableSorting: true,
        cell: renderMoneyCell,
      },
      {
        id: "payment_date",
        header: "Payment Date",
        accessorKey: "payment_date",
        minWidth: 130,
        enableSorting: true,
        cell: renderCivilDateCell,
      },
      {
        id: "success_fee",
        header: "Success Fee",
        accessorKey: "success_fee",
        minWidth: 110,
        enableSorting: true,
        cell: (value: number | null | undefined) => (
          <Typography
            noWrap
            sx={{ fontWeight: 500, textAlign: "right", width: "100%" }}
          >
            {typeof value === "number" ? formatCurrency(value) : "—"}
          </Typography>
        ),
      },
    ],
    [],
  );

  // Filter columns based on visibility settings, pinned columns first
  const visibleColumns = useMemo(() => {
    const visible = columns.filter((col) =>
      isSuccessFeesPerClaimColumnVisible(col.id),
    );
    const pinned = visible.filter((col) =>
      isSuccessFeesPerClaimColumnPinned(col.id),
    );
    const unpinned = visible.filter(
      (col) => !isSuccessFeesPerClaimColumnPinned(col.id),
    );
    return [...pinned, ...unpinned];
  }, [
    columns,
    isSuccessFeesPerClaimColumnVisible,
    successFeesPerClaimColumnVisibility,
    isSuccessFeesPerClaimColumnPinned,
    successFeesPerClaimColumnPins,
  ]);

  return { visibleColumns };
};

// Actions (CSV download, column visibility) for the per-claim success fee
// table, rendered separately from the table itself so the dashboard can
// place them beside the tabs instead of inside the card body.
export const SuccessFeeByClaimCardActions: FC = observer(() => {
  const {
    successFeesPerClaim,
    isSuccessFeesPerClaimLoading,
    downloadSuccessFeesPerClaimCsv,
  } = useObservabilityStore();
  const notifier = useNotifier();
  const { visibleColumns } = useSuccessFeesPerClaimColumns();

  const handleDownload = useCallback(async () => {
    const result = await downloadSuccessFeesPerClaimCsv(
      visibleColumns.map((col) => col.id),
    );

    if (!result.success && result.error) notifier.showError(result.error);
  }, [downloadSuccessFeesPerClaimCsv, notifier, visibleColumns]);

  const hasBillingPermission = useHasPermission(PERMISSIONS.billing);

  if (!hasBillingPermission) return null;

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mr: 3 }}>
      <UsageDownloadCsv
        onDownload={handleDownload}
        disabled={isSuccessFeesPerClaimLoading || !successFeesPerClaim?.length}
      />
      <SuccessFeeByClaimColumnVisibilityButton />
    </Box>
  );
});

// One row per claim (REV-1824): the claim's single recovery chain. However
// many appeal chats a claim has in the activity feed, its success fee appears
// exactly once here — recovery is charged once per claim, not once per appeal
// letter.
const SuccessFeeByClaimCard: FC = observer(() => {
  const {
    isSuccessFeesPerClaimLoading,
    successFeesPerClaim,
    loadSuccessFeesPerClaim,
    isSuccessFeesPerClaimColumnPinned,
    toggleSuccessFeesPerClaimColumnPin,
    successFeesPerClaimOptions,
    // Client-side sorting (the set is small and unpaginated; the table sorts it
    // itself via enableManualSorting). Empty = backend order, newest appeal
    // first. Kept in the store so the CSV download uses the same sort/filters.
    successFeesPerClaimSort,
    setSuccessFeesPerClaimSort,
    successFeesPerClaimColumnFilters: columnFilters,
    setSuccessFeesPerClaimColumnFilter,
    clearSuccessFeesPerClaimColumnFilter,
  } = useObservabilityStore();
  const hasBillingPermission = useHasPermission(PERMISSIONS.billing);
  const { visibleColumns } = useSuccessFeesPerClaimColumns();

  useEffect(() => {
    if (hasBillingPermission) loadSuccessFeesPerClaim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBillingPermission]);

  const filteredData = useMemo(
    () =>
      (successFeesPerClaim ?? []).filter((row) =>
        rowMatchesFilters(row, columnFilters),
      ),
    [successFeesPerClaim, columnFilters],
  );

  // Backend options span all report data; fall back to the loaded rows when a
  // column has none. Options are keyed on their values (user is an id, labels
  // come from getOptionLabel) so people sharing a display name stay
  // separately selectable.
  const textFilterOptions = useMemo(() => {
    const rows = successFeesPerClaim ?? [];

    const fromOptions = (
      columnId: "claim_number" | "payer" | "user_displayName",
    ) => {
      const options =
        successFeesPerClaimOptions[OPTIONS_KEY_BY_COLUMN[columnId]];
      if (options?.length) {
        return options.map((option) => String(option.value));
      }
      return columnId === "user_displayName"
        ? Array.from(new Set(rows.map((row) => String(row.user_id ?? "")).filter(Boolean)))
        : uniqueColumnValues(rows, columnId);
    };

    return {
      claim_number: fromOptions("claim_number"),
      payer: fromOptions("payer"),
      user_displayName: fromOptions("user_displayName"),
    };
  }, [successFeesPerClaim, successFeesPerClaimOptions]);

  const columnFiltersConfig: ColumnFiltersConfig = useMemo(
    () => ({
      filters: columnFilters,
      getOptions: (columnId) =>
        textFilterOptions[columnId as keyof typeof textFilterOptions] ?? [],
      onApply: setSuccessFeesPerClaimColumnFilter,
      onClear: clearSuccessFeesPerClaimColumnFilter,
      filterableColumns: [...FILTERABLE_COLUMNS],
      columnFilterTypes: COLUMN_FILTER_TYPES,
      getOptionLabel: (columnId: string, value: string) => {
        if (columnId !== "user_displayName") return value;
        const options =
          successFeesPerClaimOptions[OPTIONS_KEY_BY_COLUMN[columnId]];
        return (
          options?.find((option) => String(option.value) === value)?.label ??
          value
        );
      },
      isColumnPinned: isSuccessFeesPerClaimColumnPinned,
      onTogglePin: toggleSuccessFeesPerClaimColumnPin,
      canPinColumn: (columnId: string) => columnId !== "claim_number",
    }),
    [
      columnFilters,
      setSuccessFeesPerClaimColumnFilter,
      clearSuccessFeesPerClaimColumnFilter,
      textFilterOptions,
      successFeesPerClaimOptions,
      isSuccessFeesPerClaimColumnPinned,
      toggleSuccessFeesPerClaimColumnPin,
    ],
  );

  const filteredTotal = useMemo(
    () => filteredData.reduce((sum, row) => sum + (row.success_fee ?? 0), 0),
    [filteredData],
  );

  if (!hasBillingPermission) return null;

  return (
    <CoreCard sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <CoreVirtualizedTable<SuccessFeePerClaimRow>
          isResizable
          pinColumns
          isLoading={isSuccessFeesPerClaimLoading}
          enableSorting
          enableManualSorting
          sorting={successFeesPerClaimSort}
          onSortingChange={setSuccessFeesPerClaimSort}
          disableFirstOfTypeStyles
          data={filteredData}
          columns={visibleColumns}
          height="100%"
          rowHeight={ROW_HEIGHT}
          minWidth={320}
          getRowId={(row) => row.claim_number}
          emptyMessage="No appealed claims in range"
          columnFilters={columnFiltersConfig}
        />
      </Box>

      <Box
        sx={{
          mt: 1,
          pt: 1,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {filteredData.length.toLocaleString()}
          {filteredData.length !== (successFeesPerClaim?.length ?? 0)
            ? ` of ${(successFeesPerClaim?.length ?? 0).toLocaleString()}`
            : ""}{" "}
          claims — one success fee per claim
        </Typography>
        {filteredData.length > 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            <span>
              Total:{" "}
              <b style={{ color: "inherit" }}>
                {formatCurrency(filteredTotal)}
              </b>
            </span>
            <Tooltip title="Also includes unbilled transactions." arrow  placement="top">
              <Box
                component="span"
                sx={{ display: "inline-flex", cursor: "help" }}
              >
                <RiInformation2Line size={14} />
              </Box>
            </Tooltip>
          </Typography>
        ) : null}
      </Box>
    </CoreCard>
  );
});

export default SuccessFeeByClaimCard;
